import React from "react";
import FieldError from "./FieldError";
import { fieldAnchorProps, fieldErrorId } from "./checkerValidation";
import { eligibilityChoiceDimStyle } from "./checkerFieldStyles";

/**
 * Single-select "card" option for the eligibility wizard.
 *
 * A native, visually hidden radio sits inside the label so the option is
 * keyboard operable (arrow keys move within the group, Space selects) and
 * announced as a radio button by screen readers. The visual dot + frame keep
 * the existing card look (theme section 13 recolours `border-blue-400`).
 */
export function ChoiceCard({
  name,
  value,
  checked = false,
  onChange,
  title,
  description,
  groupAnswered = false,
  disabled = false,
  className = "",
}) {
  return (
    <label
      data-testid="eligibility-choice-card"
      style={eligibilityChoiceDimStyle(checked, groupAnswered)}
      className={`
        group relative flex items-center gap-2.5 rounded-lg px-3 py-2 min-h-[40px]
        transition-colors duration-200 cursor-pointer select-none
        ${checked ? "bg-white/20 border-2 border-blue-400" : "border-2 border-transparent"}
        ${disabled ? "cursor-not-allowed opacity-60" : ""}
        ${className}
      `}
    >
      <input
        type="radio"
        name={name}
        value={String(value)}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange?.(value)}
        className="sr-only peer"
      />
      <div
        aria-hidden
        className={`
          flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center
          peer-focus-visible:ring-2 peer-focus-visible:ring-blue-400/40 peer-focus-visible:ring-offset-2
          ${checked ? "border-blue-400 bg-blue-400" : "border-blue-400 bg-transparent"}
        `}
      >
        {checked ? <div className="w-2 h-2 rounded-full bg-white" /> : null}
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-[13px] font-normal leading-5" style={{ color: "#4B5563" }}>
          {title}
        </span>
        {description ? (
          <p className="mt-0.5 text-[12px] leading-5" style={{ color: "#6B6560" }}>
            {description}
          </p>
        ) : null}
      </div>
    </label>
  );
}

/**
 * Fieldset wrapper for a group of ChoiceCards (or checkboxes). The legend is
 * the visible question; `error` renders inline and is announced through
 * `aria-describedby` on the group.
 *
 * `variant="question"` is for sentence-length legends (kept in sentence case),
 * `variant="caption"` (default) for short field names.
 */
export function ChoiceGroup({
  name,
  legend,
  required = false,
  hint,
  note,
  error,
  role = "radiogroup",
  variant = "caption",
  icon = null,
  children,
  className = "",
  optionsClassName = "space-y-2",
}) {
  const describedBy = [hint ? `${name}-hint` : "", error ? fieldErrorId(name) : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <fieldset
      {...fieldAnchorProps(name)}
      role={role}
      tabIndex={-1}
      aria-required={required || undefined}
      aria-invalid={role === "radiogroup" && error ? true : undefined}
      aria-describedby={describedBy || undefined}
      className={`checker-fieldset min-w-0 border-0 p-0 m-0 space-y-3 ${className}`}
    >
      <legend
        className={`${variant === "question" ? "checker-question" : "checker-legend-caption"} font-medium p-0`}
        style={{ color: "#4B5563" }}
      >
        {icon}
        {legend}
        {required ? <span style={{ color: "#4B5563" }}> *</span> : null}
      </legend>
      {hint ? (
        <p id={`${name}-hint`} className="text-sm" style={{ color: "#4B5563" }}>
          {hint}
        </p>
      ) : null}
      <div className={optionsClassName}>{children}</div>
      {note ? (
        <p className="text-xs leading-relaxed" style={{ color: "#4B5563" }} data-testid={`${name}-note`}>
          {note}
        </p>
      ) : null}
      <FieldError name={name} message={error} />
    </fieldset>
  );
}

/**
 * Yes / No pair stored as a boolean. `yesValue` is the boolean written when the
 * user answers "Yes" (declarations such as `cpf_compliance_ok` store `false`
 * for "Yes, we have an issue").
 */
export function YesNoChoice({
  name,
  value,
  onChange,
  yesValue = true,
  yesLabel = "Yes",
  noLabel = "No",
  yesDescription,
  noDescription,
}) {
  const answered = value === true || value === false;
  const noValue = !yesValue;
  return (
    <>
      <ChoiceCard
        name={name}
        value="yes"
        checked={answered && value === yesValue}
        groupAnswered={answered}
        onChange={() => onChange(yesValue)}
        title={yesLabel}
        description={yesDescription}
      />
      <ChoiceCard
        name={name}
        value="no"
        checked={answered && value === noValue}
        groupAnswered={answered}
        onChange={() => onChange(noValue)}
        title={noLabel}
        description={noDescription}
      />
    </>
  );
}

export default ChoiceCard;
